import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

// Safe axios — never triggers the global logout interceptor
const getBaseURL = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== "undefined" && window.location.hostname !== "localhost") {
    return window.location.origin;
  }
  return "http://localhost:5000";
};

const safeAxios = axios.create({ baseURL: getBaseURL() });
safeAxios.interceptors.request.use((config) => {
  // Check all possible token storage keys
  const token = localStorage.getItem("superToken")
    || localStorage.getItem("token")
    || sessionStorage.getItem("superToken")
    || sessionStorage.getItem("token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Roles available to Super Admin
const ADMIN_ROLES = [
  { label: "Akagari",  endpoint: "/get_akagari" },
  { label: "Mudugudu", endpoint: "/get_mudugudu" },
  { label: "Abandi",   endpoint: "/get_abandi" },
];

const inputCls = "w-full px-3 py-2.5 text-sm rounded-lg outline-none transition-all";
const inputStyle = { border: "1.5px solid #d1d9e6", background: "#f8fafc", color: "#1e3a5f" };
const inputFocusStyle = { border: "1.5px solid #003366", background: "#fff", boxShadow: "0 0 0 3px rgba(0,51,102,0.08)" };

const StyledInput = (props) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      className={inputCls}
      style={{ ...inputStyle, ...(focused ? inputFocusStyle : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const StyledSelect = (props) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      className={inputCls}
      style={{ ...inputStyle, ...(focused ? inputFocusStyle : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Alert = ({ msg }) => {
  if (!msg?.text) return null;
  const s = {
    success: { bg: "#dcfce7", color: "#166534", border: "#86efac", icon: "check-circle-fill" },
    danger:  { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5", icon: "exclamation-triangle-fill" },
    warning: { bg: "#fef3c7", color: "#92400e", border: "#fcd34d", icon: "exclamation-triangle-fill" },
  }[msg.type] || { bg: "#fef3c7", color: "#92400e", border: "#fcd34d", icon: "exclamation-triangle-fill" };
  return (
    <div className="flex items-center gap-2 p-3 rounded-lg text-sm font-medium mb-4"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      <i className={`bi bi-${s.icon}`} />
      {msg.text}
    </div>
  );
};

// Detect if current user is Super Admin
const isSuperAdmin = () => {
  try {
    // If superToken exists, definitely super admin
    if (localStorage.getItem("superToken")) return true;
    // Check regular token — super admin has no 'role' field
    const token = localStorage.getItem("token");
    if (!token) return false;
    const payload = JSON.parse(atob(token.split(".")[1]));
    // Mudugudu/Akagari/Abandi tokens all have no 'role' at top level too
    // but super admin token has no 'role' AND no 'sup_id' pointing to another user
    // Best signal: super admin navigates to /sys-root/dashboard or /codeadd123/regsup
    const path = window.location.pathname;
    if (path.includes('/sys-root') || path.includes('/codeadd123') || path.includes('/regsup')) return true;
    return !payload.role && !payload.mudugudu_id;
  } catch { return false; }
};

const SetPassword = () => {
  const isAdmin = isSuperAdmin();

  const [roles]             = useState(ADMIN_ROLES);
  const [role, setRole]     = useState(ADMIN_ROLES[0]);
  const [users, setUsers]   = useState([]);
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState({ text: "", type: "" });

  const fetchUsers = useCallback(async (selectedRole) => {
    setLoading(true);
    setUserId("");
    setUsers([]);
    setMsg({ text: "", type: "" });
    try {
      const res = await safeAxios.get(selectedRole.endpoint);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // Show error but DO NOT logout
      setMsg({ text: "Ntibyashobotse gukuramo abakoresha: " + (err.response?.data?.error || err.message), type: "danger" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(role); }, [role, fetchUsers]);

  if (!isAdmin) return null;

  const handleRoleChange = (e) => {
    const found = roles.find(r => r.label === e.target.value);
    if (found) setRole(found);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId)              return setMsg({ text: "Hitamo umukoresha", type: "warning" });
    if (password.length < 6)  return setMsg({ text: "Ijambo ry'ibanga rigomba kuba nibura inyuguti 6", type: "warning" });
    if (password !== confirm)  return setMsg({ text: "Amajambo y'ibanga ntahuye", type: "warning" });

    setSaving(true);
    setMsg({ text: "", type: "" });
    try {
      const roleKey = role.label.toLowerCase();
      const cleanId = String(userId).replace(/[^0-9]/g, "");
      if (!cleanId) return setMsg({ text: "ID y'umukoresha ntiyemewe", type: "warning" });
      await safeAxios.put(`/password/update/${roleKey}/${cleanId}`, { password });
      setMsg({ text: "✅ Ijambo ry'ibanga ryahinduwe neza!", type: "success" });
      setPassword("");
      setConfirm("");
      setUserId("");
    } catch (err) {
      setMsg({ text: err.response?.data?.error || "Habaye ikosa mu guhindura ijambo ry'ibanga", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const selectedUser = users.find(u => String(u.user_id) === userId);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #003366 0%, #004080 100%)",
        borderRadius: "8px", borderLeft: "5px solid #FFD700",
        padding: "20px 24px", boxShadow: "0 2px 8px rgba(0,51,102,0.2)",
      }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(255,215,0,0.15)", border: "1px solid rgba(255,215,0,0.3)" }}>
            <i className="bi bi-shield-lock-fill text-xl" style={{ color: "#FFD700" }} />
          </div>
          <div>
            <h2 className="font-bold text-white text-lg leading-tight">Guhindura Ijambo ry'Ibanga</h2>
            <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.65)" }}>
              Hindura ijambo ry'ibanga ry'umukoresha uwo ari we wese.
            </p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div style={{
        background: "#fff", border: "1px solid #d1d9e6",
        borderRadius: "8px", boxShadow: "0 1px 4px rgba(0,51,102,0.08)", overflow: "hidden",
      }}>
        <div className="px-5 py-3 flex items-center gap-2"
          style={{ borderBottom: "2px solid #003366", background: "#003366" }}>
          <i className="bi bi-key-fill" style={{ color: "#FFD700" }} />
          <h3 className="text-sm font-bold text-white">Hindura Ijambo ry'Ibanga</h3>
        </div>

        <div className="p-6">
          <Alert msg={msg} />

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Role selector */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>
                <i className="bi bi-people-fill mr-1" style={{ color: "#003366" }} />Ubwoko bw'Umukoresha
              </label>
              <StyledSelect value={role.label} onChange={handleRoleChange}>
                {roles.map(r => <option key={r.label} value={r.label}>{r.label}</option>)}
              </StyledSelect>
            </div>

            {/* User selector */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>
                <i className="bi bi-person-fill mr-1" style={{ color: "#003366" }} />Hitamo Umukoresha
              </label>
              {loading ? (
                <div className="flex items-center gap-2 py-2 text-sm" style={{ color: "#64748b" }}>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent rounded-full"
                    style={{ borderColor: "#003366", borderTopColor: "transparent" }} />
                  Gutegereza...
                </div>
              ) : (
                <StyledSelect value={userId} onChange={e => setUserId(e.target.value)}>
                  <option value="">-- Hitamo umukoresha --</option>
                  {users.map((u, i) => (
                    <option key={i} value={String(u.user_id)}>
                      {u.fullname} — {u.national_id}
                    </option>
                  ))}
                </StyledSelect>
              )}
            </div>

            {/* Selected user info */}
            {selectedUser && (
              <div className="flex items-center gap-3 p-3 rounded-lg"
                style={{ background: "#f0f4ff", border: "1px solid #c7d7f5" }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ background: "#dbeafe", color: "#1e40af" }}>
                  {selectedUser.fullname?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "#1e3a5f" }}>{selectedUser.fullname}</p>
                  <p className="text-xs" style={{ color: "#64748b" }}>{selectedUser.email} • {selectedUser.telefone}</p>
                </div>
              </div>
            )}

            {/* New password */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>
                <i className="bi bi-lock-fill mr-1" style={{ color: "#003366" }} />Ijambo ry'Ibanga Rishya
              </label>
              <div className="relative">
                <StyledInput
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Injiza ijambo ry'ibanga rishya"
                />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm"
                  style={{ color: "#64748b", background: "none", border: "none", cursor: "pointer" }}>
                  <i className={`bi bi-eye${showPw ? "-slash" : ""}`} />
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>
                <i className="bi bi-shield-check mr-1" style={{ color: "#003366" }} />Emeza Ijambo ry'Ibanga
              </label>
              <StyledInput
                type={showPw ? "text" : "password"}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Ongera injiza ijambo ry'ibanga"
              />
              {confirm && password !== confirm && (
                <p className="text-xs mt-1" style={{ color: "#dc2626" }}>
                  <i className="bi bi-exclamation-circle mr-1" />Amajambo ntahuye
                </p>
              )}
            </div>

            {/* Submit */}
            <button type="submit" disabled={saving || !userId}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all disabled:opacity-50"
              style={{ background: "#003366", color: "#FFD700", border: "none", cursor: saving || !userId ? "not-allowed" : "pointer" }}
              onMouseEnter={e => { if (!saving && userId) e.currentTarget.style.background = "#004080"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#003366"; }}>
              {saving
                ? <><span className="animate-spin inline-block w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full" />Tegereza...</>
                : <><i className="bi bi-shield-lock-fill" /> Emeza Guhindura Ijambo ry'Ibanga</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
